import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { createPortal } from "react-dom"
import SessionDetailPlayer from "../DashboardComponents/sessionDetail/SessionDetailPlayer"
import useDraggablePosition from "./useDraggablePosition"
import useActiveSessionLifecycle from "../../hooks/useActiveSessionLifecycle"
import {
  clearActiveSessionSnapshot,
  getActiveSessionSnapshot,
  syncActiveSessionSnapshot,
} from "../../helpers/activeSession"
import {
  clearSessionReturnTo,
  rememberSessionReturnTo,
  readSessionReturnTo,
} from "../../helpers/sessionReturnTo"
import { store } from "../../store"

export const FLOATING_PLAYER_TYPES = {
  SESSION_DETAIL: "session-detail",
}

const MINI_SIZE = { width: 320, height: 300 }

const PLAYER_RENDERERS = {
  [FLOATING_PLAYER_TYPES.SESSION_DETAIL]: SessionDetailPlayer,
}

const FloatingPlayerContext = createContext(null)

const FloatingPlayerShell = ({
  mode,
  children,
  onRestore,
  onClose,
  guardModalOpen = false,
}) => {
  const isMini = mode === "minimized"
  const canDrag = isMini && !guardModalOpen
  const {
    nodeRef,
    position,
    isDragging,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    consumeWasDrag,
  } = useDraggablePosition({
    enabled: canDrag,
    size: MINI_SIZE,
  })

  useEffect(() => {
    if (isMini) return undefined
    document.body.classList.add("floating-player-full-open")
    return () => {
      document.body.classList.remove("floating-player-full-open")
    }
  }, [isMini])

  useEffect(() => {
    document.body.classList.add("floating-player-open")
    return () => {
      document.body.classList.remove("floating-player-open")
      document.body.classList.remove("floating-player-full-open")
      if (!document.body.classList.contains("modal-open")) {
        document.body.style.removeProperty("overflow")
        document.body.style.removeProperty("padding-right")
      }
    }
  }, [])

  useEffect(() => {
    if (isMini) return undefined
    const onKeyDown = event => {
      if (event.key !== "Escape") return
      if (document.body.classList.contains("session-guard-modal-open")) return
      if (document.querySelector(".modal.show")) return
      onClose?.()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [isMini, onClose])

  if (typeof document === "undefined") return null

  const handleMiniClick = event => {
    if (!isMini || guardModalOpen) return
    if (event.target?.closest?.("[data-pip-no-drag]")) return
    if (document.querySelector(".modal.show")) return
    if (document.body.classList.contains("session-guard-modal-open")) return
    if (consumeWasDrag()) return
    onRestore?.()
  }

  return createPortal(
    <div
      ref={isMini ? nodeRef : undefined}
      className={`floating-player${
        isMini ? " floating-player--mini" : " floating-player--full"
      }${isMini && isDragging ? " floating-player--dragging" : ""}${
        guardModalOpen ? " floating-player--guard-modal" : ""
      }`}
      aria-hidden={guardModalOpen || undefined}
      style={
        isMini && position
          ? {
              left: `${position.x}px`,
              top: `${position.y}px`,
              right: "auto",
              bottom: "auto",
            }
          : undefined
      }
      onPointerDown={canDrag ? onPointerDown : undefined}
      onPointerMove={canDrag ? onPointerMove : undefined}
      onPointerUp={canDrag ? onPointerUp : undefined}
      onPointerCancel={canDrag ? onPointerUp : undefined}
      onClick={isMini ? handleMiniClick : undefined}
      role={isMini ? "dialog" : "presentation"}
      aria-label={isMini ? "Minimized session detail" : undefined}
    >
      <div
        className="floating-player__card"
        role={isMini ? undefined : "dialog"}
        aria-modal={isMini ? undefined : "true"}
        aria-label={isMini ? undefined : "Session detail"}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}

export const FloatingPlayerProvider = ({ children }) => {
  const [player, setPlayer] = useState(null)
  const [guardModalOpen, setGuardModalOpen] = useState(false)

  const open = useCallback(config => {
    if (!config?.type) return

    setPlayer(prev => {
      const nextPayload = config.payload || {}
      const sameInstance =
        prev &&
        prev.type === config.type &&
        String(prev.payload?.sessionId || "") ===
          String(nextPayload.sessionId || "")

      if (sameInstance) {
        return {
          ...prev,
          mode: config.mode || prev.mode,
          payload: { ...prev.payload, ...nextPayload },
        }
      }

      return {
        type: config.type,
        mode: config.mode || "full",
        payload: nextPayload,
      }
    })
  }, [])

  const close = useCallback(() => {
    const sessionId = getActiveSessionSnapshot().sessionId
    clearSessionReturnTo(sessionId)
    clearActiveSessionSnapshot()
    setGuardModalOpen(false)
    setPlayer(null)
  }, [])

  const dismiss = useCallback(() => {
    if (getActiveSessionSnapshot().sessionId) {
      setPlayer(prev => (prev ? { ...prev, mode: "minimized" } : prev))
      return
    }
    close()
  }, [close])

  const minimize = useCallback(() => {
    setPlayer(prev => (prev ? { ...prev, mode: "minimized" } : prev))
  }, [])

  const restore = useCallback(() => {
    setPlayer(prev => (prev ? { ...prev, mode: "full" } : prev))
  }, [])

  const openSessionDetail = useCallback(
    ({
      sessionId,
      projectId,
      project,
      cityId,
      city,
      mode,
      returnTo,
      returnLabel,
    } = {}) => {
      if (!sessionId) return
      const token = store.getState().auth.jid
      const prev = getActiveSessionSnapshot()
      const nextId = String(sessionId)
      syncActiveSessionSnapshot({
        sessionId: nextId,
        token,
        shouldPause:
          prev.sessionId === nextId ? prev.shouldPause : true,
      })

      const stored =
        returnTo || returnLabel ? null : readSessionReturnTo(nextId)
      const resolvedReturnTo = returnTo || stored?.path || null
      const resolvedReturnLabel = returnTo
        ? returnLabel || ""
        : returnLabel || stored?.label || ""

      if (resolvedReturnTo) {
        rememberSessionReturnTo(
          nextId,
          resolvedReturnTo,
          resolvedReturnLabel,
        )
      }

      open({
        type: FLOATING_PLAYER_TYPES.SESSION_DETAIL,
        mode,
        payload: {
          sessionId: nextId,
          projectId: projectId ? String(projectId) : null,
          project: project || null,
          cityId: cityId ? String(cityId) : null,
          city: city || null,
          ...(resolvedReturnTo
            ? {
                returnTo: resolvedReturnTo,
                returnLabel: resolvedReturnLabel,
              }
            : {}),
        },
      })
    },
    [open],
  )

  const setActiveSessionGuard = useCallback(
    ({ sessionId = null, isRunning = false } = {}) => {
      const token = store.getState().auth.jid
      if (!sessionId) {
        clearActiveSessionSnapshot()
        return
      }
      syncActiveSessionSnapshot({
        sessionId,
        token,
        shouldPause: Boolean(isRunning),
      })
    },
    [],
  )

  useActiveSessionLifecycle({
    openSessionDetail,
    close,
    player,
  })

  const value = useMemo(
    () => ({
      player,
      open,
      close,
      minimize,
      restore,
      openSessionDetail,
    }),
    [player, open, close, minimize, restore, openSessionDetail],
  )

  const Renderer = player ? PLAYER_RENDERERS[player.type] : null

  return (
    <FloatingPlayerContext.Provider value={value}>
      {children}
      {player && Renderer && (
        <FloatingPlayerShell
          mode={player.mode}
          guardModalOpen={guardModalOpen}
          onRestore={restore}
          onClose={dismiss}
        >
          <Renderer
            payload={player.payload}
            mode={player.mode}
            onMinimize={minimize}
            onRestore={restore}
            onClose={dismiss}
            onEnded={close}
            onActiveChange={setActiveSessionGuard}
            onGuardModalChange={setGuardModalOpen}
          />
        </FloatingPlayerShell>
      )}
    </FloatingPlayerContext.Provider>
  )
}

export const useFloatingPlayer = () => {
  const context = useContext(FloatingPlayerContext)
  if (!context) {
    throw new Error(
      "useFloatingPlayer must be used within FloatingPlayerProvider",
    )
  }
  return context
}

export const useSessionDetailPlayer = () => {
  const { openSessionDetail, close, minimize, restore, player } =
    useFloatingPlayer()
  return {
    openSessionDetail,
    close,
    minimize,
    restore,
    isOpen: player?.type === FLOATING_PLAYER_TYPES.SESSION_DETAIL,
    mode: player?.mode || null,
    sessionId: player?.payload?.sessionId || null,
  }
}

export default FloatingPlayerProvider
